import { SocioRow } from '../../constitution-form/models/socio-row';
import { formatTituloLabel } from '../../constitution-form/utils/titulo-label.util';
import { formatShareAmountEs } from '../../constitution-form/utils/format-share-amount';
import { serieTieneAcciones } from '../../constitution-form/utils/series-with-shares.util';
import { applyTransmision } from '../utils/transmision-socios.util';
import { AmortizacionRango, applyAmortizacion, extractAmortizedShares } from '../utils/reduccion-socios.util';
import { findLatestOperationForSection } from '../utils/estado-actual.util';
import { mapToOperacionSocietaria } from '../operaciones-societarias-form.component';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  signal,
  computed,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormsModule, FormBuilder, FormGroup, FormArray } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faCheck, faXmark, faUpload, faFile, faTrash } from '@fortawesome/pro-light-svg-icons';
import { CalendarModule } from 'primeng/calendar';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { DropdownModule } from 'primeng/dropdown';
import { FileUploadModule } from 'primeng/fileupload';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';

import {
  OperacionCategoria,
  OperacionSubtipo,
  OperacionSocietaria,
  getCategoriaConfig,
  getSubtipoConfig,
  getSeccionesAfectadas,
} from '../mock-data/operaciones.mock';
import { ConstitutionSnapshot, SectionKey } from '../models/constitution-snapshot.model';

import { ConstitutionFormStore } from '../../constitution-form/stores/constitution-form.store';
import { ConstitutionCapitalStore } from '../../constitution-form/stores/constitution-capital.store';
import { SerieData } from '../../constitution-form/models/serie-data';
import { ConstitutionShareholdersStore } from '../../constitution-form/stores/constitution-shareholders.store';
import { ConstitutionAdministrationStore } from '../../constitution-form/stores/constitution-administration.store';
import { ConstitutionAttorniesStore } from '../../constitution-form/stores/constitution-attornies.store';

import { ConstitutionDeedObjectSectionComponent } from '../../constitution-form/sections/constitution-deed-object-section/constitution-deed-object-section.component';
import { ConstitutionCapitalSeriesSectionComponent } from '../../constitution-form/sections/constitution-capital-series-section/constitution-capital-series-section.component';
import { ConstitutionSociosSectionComponent } from '../../constitution-form/sections/constitution-socios-section/constitution-socios-section.component';
import { ConstitutionAdministrationSectionComponent } from '../../constitution-form/sections/constitution-administration-section/constitution-administration-section.component';
import { ConstitutionRepresentativesComponent } from '../../constitution-form/sections/constitution-representatives/constitution-representatives.component';
import { IdentificationFormComponent } from '../../identification-form/identification-form.component';
import { mapSociosOperationToSnapshots } from '../../constitution-form/utils/socio-snapshot.mapper';
import { mapAdministrationToSnapshot } from '../../constitution-form/utils/admin-snapshot.mapper';
import { mapOperationAttorneyToSnapshot } from '../../constitution-form/utils/attorney-snapshot.mapper';
import { formatEuro } from '../../constitution-form/utils/socios-display.util';

import { DOMICILES, PROVINCES } from '../../../../core/constants/client.constants';
import { CorporateOperationsService } from '../../../../core/services/clients/corporate-operations.service';
import { ClientsService } from '../../../../core/services/clients/clients.service';
import { CorporateOperation, CorporateOperationFile, CorporateOperationCreateDTO, CorporateOperationUpdateDTO } from '../../../../core/models/corporate-operation.model';
import { parseDateOnlyToLocal, toDateOnlyPayload } from '../../../../core/utils/date-only.util';

// ── Step key types ────────────────────────────────────────────────────────────

export type CrossTabStepKey = 'step_denominacion' | 'step_domicilio' | 'step_articulado';
export type AmpliacionStepKey = 'step_ampliacion_tipo' | 'step_ampliacion_datos';
export type ReduccionStepKey = 'step_reduccion_tipos';
export type ContrasplitStepKey = 'step_contrasplit';
export type TransmisionStepKey = 'step_transmision';
export type FusionStepKey = 'step_fusion';
export type TransformacionStepKey = 'step_transformacion';
export type WizardStepKey = SectionKey | 'escritura_meta' | 'resumen' | CrossTabStepKey | AmpliacionStepKey | ReduccionStepKey | ContrasplitStepKey | TransmisionStepKey | FusionStepKey | TransformacionStepKey;

export interface WizardStep {
  key: WizardStepKey;
  label: string;
}

const STEP_LABELS: Record<WizardStepKey, string> = {
  escritura_meta: 'Datos de escritura',
  step_denominacion: 'Razón social',
  step_domicilio: 'Domicilio social',
  step_articulado: 'Articulado',
  step_ampliacion_tipo: 'Tipo de ampliación',
  step_ampliacion_datos: 'Datos de ampliación',
  step_reduccion_tipos: 'Tipos de reducción',
  step_contrasplit: 'Contrasplit',
  step_transmision: 'Transmisión de acciones',
  step_fusion: 'Fusión',
  step_transformacion: 'Identificación',
  escritura: 'Escritura',
  objetoSocial: 'Objeto Social',
  capitalSocial: 'Capital Social',
  socios: 'Socios',
  administracion: 'Administración',
  apoderados: 'Apoderados',
  resumen: 'Resumen',
};

/** Converts a dd/mm/yy or dd/mm/yyyy calendar string to ISO yyyy-MM-dd for API transport. */
function ddmmToISO(val: string | null | undefined): string | undefined {
  if (!val) return undefined;
  const parts = val.split('/');
  if (parts.length !== 3) return val || undefined;
  const [d, m, y] = parts;
  const fullYear = y.length === 2 ? '20' + y : y;
  return `${fullYear}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
}

/** Ensures a calendar string is displayed as dd/mm/yyyy (4-digit year). */
function ddmmToDisplay(val: string | null | undefined): string {
  if (!val) return '';
  const parts = val.split('/');
  if (parts.length !== 3) return val;
  const [d, m, y] = parts;
  const fullYear = y.length === 2 ? '20' + y : y;
  return `${d.padStart(2, '0')}/${m.padStart(2, '0')}/${fullYear}`;
}

/** Converts an ISO date string (yyyy-MM-dd or ISO 8601) to dd/mm/yyyy display format. */
function isoToDisplay(iso: string | Date | null | undefined): string {
  const d = parseDateOnlyToLocal(iso);
  if (!d) return '';
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

/** Maps cross-tab subtypes to their dedicated wizard step key */
const CROSS_TAB_STEP: Partial<Record<OperacionSubtipo, CrossTabStepKey>> = {
  denominacion_social: 'step_denominacion',
  domicilio_social: 'step_domicilio',
  articulado_estatutos: 'step_articulado',
};

/** "SERIE A (1 – 1.000)": nombre y numeración de la serie con separador de miles. */
function serieLabel(s: SerieData): string {
  const hasta = s.sharesNumberTo ?? s.totalShares;
  return `${s.name} (${formatShareAmountEs(s.sharesNumberFrom ?? 1)} – ${hasta != null ? formatShareAmountEs(hasta) : '?'})`;
}

@Component({
  selector: 'app-operation-wizard',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    FontAwesomeModule,
    CalendarModule,
    InputTextModule,
    InputTextareaModule,
    DropdownModule,
    FileUploadModule,
    CheckboxModule,
    InputNumberModule,
    ConstitutionDeedObjectSectionComponent,
    ConstitutionCapitalSeriesSectionComponent,
    ConstitutionSociosSectionComponent,
    ConstitutionAdministrationSectionComponent,
    ConstitutionRepresentativesComponent,
    IdentificationFormComponent,
  ],
  providers: [
    ConstitutionFormStore,
    ConstitutionCapitalStore,
    ConstitutionShareholdersStore,
    ConstitutionAdministrationStore,
    ConstitutionAttorniesStore,
  ],
  templateUrl: './operation-wizard.component.html',
  styleUrl: './operation-wizard.component.scss',
})
export class OperationWizardComponent implements OnInit {
  @ViewChild('identificationStep') identificationStep?: IdentificationFormComponent;
  @Input() categoria!: OperacionCategoria;
  @Input() subtipo: OperacionSubtipo | null = null;
  @Input() clientId: number = 0;
  @Input() existingOperation: CorporateOperation | null = null;
  @Output() cancel = new EventEmitter<void>();
  @Output() complete = new EventEmitter<OperacionSocietaria>();

  get isEditMode(): boolean {
    return this.existingOperation !== null;
  }

  readonly store = inject(ConstitutionFormStore);
  private readonly capitalStore = inject(ConstitutionCapitalStore);
  private readonly shareholdersStore = inject(ConstitutionShareholdersStore);
  private readonly administrationStore = inject(ConstitutionAdministrationStore);
  private readonly attorniesStore = inject(ConstitutionAttorniesStore);
  private readonly corporateOpsService = inject(CorporateOperationsService);
  private readonly clientsService = inject(ClientsService);

  readonly faCheck = faCheck;
  readonly faXmark = faXmark;
  readonly faUpload = faUpload;
  readonly faFile = faFile;
  readonly faTrash = faTrash;

  readonly currentStep = signal(0);
  readonly isSaving = signal(false);
  readonly isInitializing = signal(true);

  /** Razón social vigente al abrir el paso (antes de editar el campo único). */
  private denominacionPreviousName = '';

  /** Operation data overlay for embedded identification step (edit transformación). */
  identificationOperationOverlay: Record<string, unknown> | null = null;

  /** Last serialized identification payload (survives step visibility / ViewChild timing). */
  private identificationOperationDataCache: Record<string, unknown> | null = null;

  // ── Dropdown options (mock data, no API) ───────────────────────────────────
  readonly domicileOptions = DOMICILES;
  readonly provinceOptions = PROVINCES;

  readonly tipoAmpliacionOptions = [
    { label: 'Dineraria', value: 'dineraria' },
    { label: 'No dineraria', value: 'no_dineraria' },
    { label: 'Régimen especial Canje de Valores', value: 'canje_valores' },
  ];

  readonly modoAmpliacionOptions = [
    { label: 'Ampliación VN (Valor Nominal)', value: 'ampliacion_vn' },
    { label: 'Ampliación acciones', value: 'ampliacion_acciones' },
  ];

  // ── Ampliación: series from store + "create new" option ───────────────────
  readonly seriesOptions = computed(() => {
    const existing = this.existingSeriesOptions();
    return [...existing, { label: '+ Crear nueva serie', value: '__new__' as any }];
  });

  /** Solo series con acciones: una serie vacía no se puede elegir. */
  readonly existingSeriesOptions = computed(() =>
    this.capitalStore.series().filter(serieTieneAcciones).map(s => ({
      label: serieLabel(s),
      value: s.id,
    })),
  );

  // ── Attached files ─────────────────────────────────────────────────────────
  attachedFiles: File[] = [];

  // ── Cross-tab step key (null when not a cross-tab subtype) ─────────────────
  get crossTabStepKey(): CrossTabStepKey | null {
    if (!this.subtipo) return null;
    return CROSS_TAB_STEP[this.subtipo] ?? null;
  }

  // ── Sections affected ──────────────────────────────────────────────────────
  get seccionesAfectadas(): SectionKey[] {
    return getSeccionesAfectadas(this.categoria, this.subtipo);
  }

  // ── Description / tab info (shown as info note inside each cross-tab step) ─
  get descripcion(): string {
    if (this.subtipo) return getSubtipoConfig(this.subtipo)?.descripcion ?? '';
    return getCategoriaConfig(this.categoria)?.descripcion ?? '';
  }

  get tabAfectada(): { key: string; label: string; accion: string } | undefined {
    if (this.subtipo) return getSubtipoConfig(this.subtipo)?.tabAfectada;
    return getCategoriaConfig(this.categoria)?.tabAfectada;
  }

  // ── Ampliación-specific ──────────────────────────────────────────────────
  get isAmpliacion(): boolean {
    return this.subtipo === 'ampliacion_capital';
  }

  // ── Reducción-specific ──────────────────────────────────────────────────
  get isReduccion(): boolean {
    return this.subtipo === 'reduccion_capital';
  }

  // ── Acordeón-specific ──────────────────────────────────────────────────
  get isAcordeon(): boolean {
    return this.subtipo === 'operacion_acordeon';
  }

  // ── Contrasplit-specific ────────────────────────────────────────────────
  get isContrasplit(): boolean {
    return this.subtipo === 'contrasplit';
  }

  // ── Compraventa / transmisión-specific ──────────────────────────────────
  get isCompraventa(): boolean {
    return this.subtipo === 'compraventa_donacion_herencia' || this.subtipo === 'fusion_socios_filial';
  }

  // ── Fusión absorbente-specific ───────────────────────────────────────────
  get isFusionAbsorbente(): boolean {
    return this.subtipo === 'fusion_absorbente';
  }

  // ── Escisión-specific ────────────────────────────────────────────────────
  get isEscision(): boolean {
    return this.subtipo === 'escision';
  }

  // ── Transformación-specific ──────────────────────────────────────────────
  get isTransformacion(): boolean {
    return this.subtipo === 'cambio_forma_juridica';
  }

  // ── Steps ──────────────────────────────────────────────────────────────────
  readonly steps = computed<WizardStep[]>(() => {
    const base: WizardStep[] = [{ key: 'escritura_meta', label: STEP_LABELS['escritura_meta'] }];

    if (this.crossTabStepKey) {
      base.push({ key: this.crossTabStepKey, label: STEP_LABELS[this.crossTabStepKey] });
    } else {
      if (this.isAmpliacion) {
        base.push(
          { key: 'step_ampliacion_tipo', label: STEP_LABELS['step_ampliacion_tipo'] },
          { key: 'step_ampliacion_datos', label: STEP_LABELS['step_ampliacion_datos'] },
        );
      }
      if (this.isReduccion) {
        base.push(
          { key: 'step_reduccion_tipos', label: STEP_LABELS['step_reduccion_tipos'] },
        );
      }
      if (this.isAcordeon) {
        base.push(
          { key: 'step_reduccion_tipos', label: STEP_LABELS['step_reduccion_tipos'] },
          { key: 'step_ampliacion_tipo', label: STEP_LABELS['step_ampliacion_tipo'] },
          { key: 'step_ampliacion_datos', label: STEP_LABELS['step_ampliacion_datos'] },
        );
      }
      if (this.isContrasplit) {
        base.push({ key: 'step_contrasplit', label: STEP_LABELS['step_contrasplit'] });
      }
      if (this.isEscision) {
        base.push({ key: 'step_reduccion_tipos', label: STEP_LABELS['step_reduccion_tipos'] });
      }
      if (this.isCompraventa) {
        base.push({ key: 'step_transmision', label: STEP_LABELS['step_transmision'] });
      }
      if (this.isFusionAbsorbente) {
        base.push({ key: 'step_fusion', label: STEP_LABELS['step_fusion'] });
      }
      if (this.isTransformacion) {
        base.push({ key: 'step_transformacion', label: STEP_LABELS['step_transformacion'] });
      }
      base.push(
        ...this.seccionesAfectadas.map(k => ({
          key: k as WizardStepKey,
          label: STEP_LABELS[k as WizardStepKey] ?? k,
        }))
      );
    }

    base.push({ key: 'resumen', label: STEP_LABELS['resumen'] });
    return base;
  });

  readonly totalSteps = computed(() => this.steps().length);
  readonly isLastStep = computed(() => this.currentStep() === this.totalSteps() - 1);
  readonly isFirstStep = computed(() => this.currentStep() === 0);
  readonly currentStepKey = computed(() => this.steps()[this.currentStep()]?.key ?? 'escritura_meta');

  // ── Resumen helpers ────────────────────────────────────────────────────────
  readonly isResumen = computed(() => this.currentStepKey() === 'resumen');

  hasStep(key: WizardStepKey): boolean {
    return this.steps().some(s => s.key === key);
  }

  // ── Forms ──────────────────────────────────────────────────────────────────
  metaForm!: FormGroup;
  crossTabForm!: FormGroup;
  ampTipoForm!: FormGroup;
  ampDatosForm!: FormGroup;
  reduccionEntries!: FormArray;
  transmisionForm!: FormGroup;
  adquirentes!: FormArray;
  fusionForm!: FormGroup;

  selectedTipoAmpliacion = signal('');
  selectedTipoFusion = signal('');
  selectedModoAmpliacion = signal('');

  readonly tipoReduccionOptions = [
    { label: 'Disminución VN', value: 'disminucion_vn' },
    { label: 'Amortización acciones', value: 'amortizacion_acciones' },
  ];

  readonly tipoContrasplitOptions = [
    { label: 'Ampliación VN', value: 'disminucion_vn' },
    { label: 'Amortización acciones', value: 'amortizacion_acciones' },
  ];

  /** Reestructuración: fusión socios filial adds "Fusión" to the transmission types. */
  get tipoTransmisionOptions() {
    return this.subtipo === 'fusion_socios_filial'
      ? [...this.baseTipoTransmisionOptions, { label: 'Fusión', value: 'fusion' }]
      : this.baseTipoTransmisionOptions;
  }

  private readonly baseTipoTransmisionOptions = [
    { label: 'Compraventa', value: 'compraventa' },
    { label: 'Donación', value: 'donacion' },
    { label: 'Herencia', value: 'herencia' },
    { label: 'Permuta', value: 'permuta' },
    { label: 'Régimen especial de Canje de Valores', value: 'canje_valores' },
  ];

  /** Transmitentes: only persons already registered as socios of the company (value = client id). */
  readonly socioOptions = computed(() =>
    this.shareholdersStore.socios()
      .filter(s => s.associatedClient?.id != null)
      .map(s => ({ label: s.name, value: s.associatedClient!.id })),
  );

  readonly transmitenteId = signal<number | null>(null);

  /** Participaciones del transmitente (una fila por numeración) con su fecha de baja. */
  bajasForm!: FormArray;

  get bajaRows(): FormGroup[] {
    return this.bajasForm.controls as FormGroup[];
  }

  get bajasTotal(): number {
    return this.bajaRows.reduce((sum, g) => sum + (Number(g.get('count')?.value) || 0), 0);
  }

  private createBajaRow(n: { seriesId: number | null; serie: string; titulo: string; desde: number; hasta: number; count: number; fechaBaja?: string }): FormGroup {
    return this.fb.group({
      seriesId: [n.seriesId],
      serie: [n.serie],
      titulo: [n.titulo],
      desde: [n.desde],
      hasta: [n.hasta],
      count: [n.count],
      fechaBaja: [n.fechaBaja ?? ''],
    });
  }

  /** Rebuilds the baja rows from the transmitente's holdings before this operation. */
  private rebuildBajaRows(transmitenteId: number | null): void {
    this.bajasForm.clear();
    if (transmitenteId == null) return;
    const source = this.sociosBaseline ?? this.shareholdersStore.socios();
    const socio = source.find(sc => sc.associatedClient?.id === transmitenteId);
    for (const n of socio?.shareNumberings ?? []) {
      this.bajasForm.push(this.createBajaRow({
        seriesId: n.seriesId ?? null,
        serie: n.seriesName || this.capitalStore.series().find(x => x.id === n.seriesId)?.name || '—',
        titulo: n.titulo ?? '',
        desde: n.sharesNumberFrom,
        hasta: n.sharesNumberTo,
        count: n.sharesCount,
      }));
    }
  }

  /** Persons available as adquirente (all clients in the system) */
  readonly personOptions = computed(() =>
    this.shareholdersStore.clients().map(c => ({ label: c.name, value: c.id }))
  );

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    this.metaForm = this.fb.nonNullable.group({
      fechaEscritura: this.fb.nonNullable.control(''),
      conceptoEscritura: this.fb.nonNullable.control(''),
      notario: this.fb.nonNullable.control(''),
      protocolo: this.fb.nonNullable.control(''),
      irus: this.fb.nonNullable.control(''),
      observaciones: this.fb.nonNullable.control(''),
      // Registro Mercantil
      provinciaRM: this.fb.nonNullable.control(''),
      fechaInscripcion: this.fb.nonNullable.control(''),
      tomo: this.fb.nonNullable.control(''),
      folio: this.fb.nonNullable.control(''),
      hoja: this.fb.nonNullable.control(''),
      seccion: this.fb.nonNullable.control(''),
      libro: this.fb.nonNullable.control(''),
      numInscripcion: this.fb.nonNullable.control(''),
    });

    this.crossTabForm = this.buildCrossTabForm();

    this.ampTipoForm = this.fb.nonNullable.group({
      tipoAmpliacion: this.fb.nonNullable.control(''),
      detalleAportaciones: this.fb.nonNullable.control(''),
      detalleCanje: this.fb.nonNullable.control(''),
    });

    this.ampDatosForm = this.fb.nonNullable.group({
      modoAmpliacion: this.fb.nonNullable.control(''),
      // Ampliación VN
      vnSerie: this.fb.nonNullable.control(''),
      vnImporteAmpliado: this.fb.nonNullable.control(''),
      vnNuevoVN: this.fb.nonNullable.control(''),
      // Series adicionales de la misma ampliación VN (la primera va en los campos vn* de arriba)
      vnExtras: this.fb.array<FormGroup>([]),
      // Ampliación acciones
      accSerie: this.fb.nonNullable.control(''),
      accNumAmpliado: this.fb.nonNullable.control(''),
      accNuevasAcciones: this.fb.nonNullable.control(''),
      accDesde: this.fb.nonNullable.control(''),
      accHasta: this.fb.nonNullable.control(''),
      // Serie nueva creada en este mismo paso (cuando accSerie === '__new__')
      accNuevaSerieNombre: this.fb.nonNullable.control(''),
      accNuevaSerieVN: this.fb.nonNullable.control(''),
      // Series adicionales de la misma ampliación de acciones (la primera va en los campos acc* de arriba)
      accExtras: this.fb.array<FormGroup>([]),
    });

    // Reducción de capital: dynamic list of reduction entries
    this.reduccionEntries = this.fb.array([this.createReduccionEntry()]);

    // Compraventa / transmisión de acciones
    this.transmisionForm = this.fb.nonNullable.group({
      tipoTransmision: this.fb.nonNullable.control(''),
      transmitente: this.fb.control<number | null>(null),
    });
    this.adquirentes = this.fb.array([this.createAdquirente()]);
    this.bajasForm = this.fb.array([]);
    this.transmisionForm.get('transmitente')!.valueChanges.subscribe(v => {
      this.transmitenteId.set(v);
      this.rebuildBajaRows(v);
    });

    // Fusión absorbente
    this.fusionForm = this.fb.nonNullable.group({
      tipoFusion: this.fb.nonNullable.control(''),
    });

    // Marcar "¿Tiene serie?" en una operación sobre un capital único: ese capital pasa a ser la primera serie.
    this.capitalStore.capitalSocialForm.get('hasSeries')?.valueChanges.subscribe(tiene => {
      if (tiene && !this.aplicandoConversion && !this.isInitializing() && this.categoria !== 'constitucion') {
        this.convertirCapitalUnicoEnPrimeraSerie();
      }
    });

    this.capitalStore.setOnSeriesChanged(() => {
      if (!this.isInitializing()) {
        this.syncSociosWithCapital();
      }
    });

    this.store.init(this.clientId || null)
      .catch(() => {
        // ignore API errors during init (e.g. no constitution yet for new operations)
      })
      .finally(async () => {
        if (this.existingOperation) {
          this.populateForEdit(this.existingOperation);
        }
        if (!this.existingOperation && this.subtipo === 'alta_baja_cambio_admin') {
          await this.prefillAdministrationFromOperations();
        }
        if (this.subtipo === 'denominacion_social') {
          await this.initDenominacionRazonSocialField();
        }
        this.syncSociosWithCapital();
        this.isInitializing.set(false);
      });
  }

  syncSociosWithCapital(): void {
    this.shareholdersStore.reconcileAllSociosWithCapital(this.capitalStore);
  }

  private async initDenominacionRazonSocialField(): Promise<void> {
    const control = this.crossTabForm.get('nuevaRazonSocial');
    const current = String(control?.value ?? '').trim();

    if (!this.denominacionPreviousName) {
      const fromClient = await this.resolveClientRazonSocial();
      if (fromClient) {
        this.denominacionPreviousName = fromClient;
      }
    }

    if (!current && this.denominacionPreviousName) {
      control?.patchValue(this.denominacionPreviousName);
    } else if (!this.denominacionPreviousName && current) {
      this.denominacionPreviousName = current;
    }
  }

  /** Starts the administración step from the same estado actual the client detail shows. */
  private async prefillAdministrationFromOperations(): Promise<void> {
    if (!this.clientId) return;
    try {
      const result = await this.corporateOpsService.getByClient(this.clientId);
      const latest = findLatestOperationForSection(
        result.map(op => mapToOperacionSocietaria(op)),
        'administracion',
      );
      const admin = latest?.operationData?.['administracion'] as
        { administrationSystem?: string | null; members?: unknown[] } | undefined;
      if (admin) this.administrationStore.hydrateFromOperation(admin);
    } catch {
      // keep what the API returned
    }
  }

  private async resolveClientRazonSocial(): Promise<string> {
    if (!this.clientId) return '';
    try {
      const c = await this.clientsService.getClientById(this.clientId);
      if (!c) return '';
      if (c.personType === 'PHYSICAL_PERSON') {
        return [c.firstSurname, c.secondSurname ? c.secondSurname + ',' : null, c.name]
          .filter(Boolean)
          .join(' ');
      }
      return c.name || '';
    } catch {
      return '';
    }
  }

  private onStepEntered(stepKey: WizardStepKey | undefined): void {
    if (stepKey === 'capitalSocial') {
      this.applyShareChangesToCapital();
    }
    if (stepKey === 'socios') {
      if (this.isCompraventa) {
        void this.applyTransmisionToSocios().then(() => this.syncSociosWithCapital());
        return;
      }
      this.syncSociosWithCapital();
    }
  }

  // ── Transmisión → Socios ───────────────────────────────────────────────────
  /** Socios before this transmisión; re-applied from scratch each time the socios step is entered. */
  private sociosBaseline: SocioRow[] | null = null;
  private sociosSnapshotHydrated = false;
  private applySociosSeq = 0;
  private readonly clientProfileCache = new Map<number, { name: string; documentNumber: string; address: string }>();

  private async loadClientProfile(clientId: number): Promise<{ name: string; documentNumber: string; address: string }> {
    const cached = this.clientProfileCache.get(clientId);
    if (cached) return cached;
    const fallbackName = this.shareholdersStore.clients().find(c => c.id === clientId)?.name ?? '';
    let profile = { name: fallbackName, documentNumber: '', address: '' };
    try {
      const c = await this.clientsService.getClientById(clientId);
      if (c) {
        const name = c.personType === 'PHYSICAL_PERSON'
          ? [c.firstSurname, c.secondSurname, c.name].filter(Boolean).join(' ')
          : (c.name || '');
        profile = { name: name || fallbackName, documentNumber: c.documentNumber ?? '', address: '' };
      }
    } catch { /* keep fallback */ }
    try {
      const addresses = await this.clientsService.getClientAddresses(clientId);
      const a = addresses?.[0];
      if (a) {
        profile.address = [a.street, a.number, a.postalCode, a.town, a.province]
          .filter((p: unknown) => p != null && String(p).trim() !== '')
          .join(', ');
      }
    } catch { /* address is optional */ }
    this.clientProfileCache.set(clientId, profile);
    return profile;
  }

  private buildNewSocio(clientId: number, localId: number, profile: { name: string; documentNumber: string; address: string }): SocioRow {
    return {
      id: localId,
      associatedClient: { id: clientId },
      name: profile.name,
      documentNumber: profile.documentNumber,
      address: profile.address,
      representatives: [],
      shareNumberings: [],
      seriesId: null,
      sharesCount: 0,
      sharesNumberFrom: null,
      sharesNumberTo: null,
      isWorkerPartner: false,
      socialSecurityRegime: '',
      classification: '',
      selfEmployedChargedTo: '',
      selfEmployedChargedName: '',
      bankAccount: null,
      monthlyNetIncome: null,
      mutualAccidents: '',
      mutualCommonContingencies: '',
      isGeneralMeetingPresident: false,
      isGeneralMeetingSecretary: false,
    };
  }

  /**
   * Applies the transmisión of step 2 onto the socios store: the holdings with a fecha de baja
   * leave the transmitente and each adquirente receives the ranges informed for them.
   */
  private async applyTransmisionToSocios(): Promise<void> {
    if (this.sociosSnapshotHydrated) return;
    const seq = ++this.applySociosSeq;

    if (!this.sociosBaseline) {
      this.sociosBaseline = this.shareholdersStore.socios().map(s => ({
        ...s,
        shareNumberings: s.shareNumberings?.map(n => ({ ...n })),
      }));
    }
    const base = this.sociosBaseline;

    const adquirentes = (this.adquirentes.getRawValue() as Array<Record<string, any>>)
      .map(a => ({
        clientId: Number(a['adquirente']),
        rangos: (a['rangos'] as Array<Record<string, any>>)
          .map(r => ({
            seriesId: r['serie'] === '' || r['serie'] == null ? null : Number(r['serie']),
            seriesName: this.resolveSerieNombre(r['serie']),
            desde: this.parseEsNumber(r['desde']) ?? NaN,
            hasta: this.parseEsNumber(r['hasta']) ?? NaN,
          })),
      }))
      .filter(a => Number.isFinite(a.clientId) && a.clientId > 0);

    const profiles = new Map<number, { name: string; documentNumber: string; address: string }>();
    for (const a of adquirentes) {
      if (!base.some(s => s.associatedClient?.id === a.clientId)) {
        profiles.set(a.clientId, await this.loadClientProfile(a.clientId));
      }
    }
    if (seq !== this.applySociosSeq) return; // a newer apply superseded this one

    let nextLocalId = -1;
    const bajas = (this.bajasForm.getRawValue() as Array<Record<string, any>>)
      .filter(b => b['fechaBaja'])
      .map(b => ({ seriesId: b['seriesId'] ?? null, desde: Number(b['desde']), hasta: Number(b['hasta']) }));

    const result = applyTransmision(
      base,
      { transmitenteId: this.transmisionForm.get('transmitente')?.value ?? null, bajas, adquirentes },
      clientId => this.buildNewSocio(clientId, nextLocalId--, profiles.get(clientId) ?? { name: '', documentNumber: '', address: '' }),
    );
    this.shareholdersStore.socios.set(result);
    this.shareholdersStore.buildSociosTableConfig();
  }

  // ── Share changes → Capital Social ─────────────────────────────────────────
  /** Capital state before this operation's share changes; re-applied from scratch each time. */
  private capitalBaseline: { hasSeries: boolean; series: SerieData[]; form: Record<string, unknown> } | null = null;
  /** Ids locales de la primera serie (el capital único convertido) y de la serie nueva creada en la ampliación; estables entre recálculos. */
  private idsSerieNueva: { primera: number; nueva: number } | null = null;
  private aplicandoConversion = false;

  get accNuevaSerieActiva(): boolean {
    return this.ampDatosForm.get('accSerie')?.value === '__new__';
  }

  /** Cambio de serie en la ampliación de acciones: "+ Crear nueva serie" muestra los campos de la serie nueva en el propio paso (sin modal). */
  onAccSerieChange(): void {
    this.syncSociosWithCapital();
  }

  private idsParaSerieNueva(): { primera: number; nueva: number } {
    if (!this.idsSerieNueva) {
      const primera = this.capitalStore.nextSerieId();
      this.idsSerieNueva = { primera, nueva: primera + 1 };
      this.capitalStore.nextSerieId.set(primera + 2);
    }
    return this.idsSerieNueva;
  }

  /** El capital único pasa a ser la primera serie, sin nombre (se nombra en el paso Capital); los socios "Sin serie" pasan a ella. */
  private convertirCapitalUnicoEnPrimeraSerie(): void {
    const form = this.capitalStore.capitalSocialForm.getRawValue();
    if (this.capitalStore.series().length > 0 || !form['totalShares']) return;
    const { primera } = this.idsParaSerieNueva();
    this.aplicandoConversion = true;
    this.capitalStore.series.set([{
      id: primera, feLocalId: primera, name: '',
      totalShares: form['totalShares'], nominalValue: form['nominalValue'],
      sharesNumberFrom: form['sharesNumberFrom'], sharesNumberTo: form['sharesNumberTo'],
    } as SerieData]);
    this.aplicandoConversion = false;
    this.capitalStore.buildSeriesTableConfig();
    this.asignarSociosSinSerie(primera, '');
  }

  private asignarSociosSinSerie(serieId: number, serieName: string): void {
    this.shareholdersStore.socios.update(list => list.map(s => ({
      ...s,
      shareNumberings: s.shareNumberings?.map(n => n.seriesId == null ? { ...n, seriesId: serieId, seriesName: serieName } : n),
    })));
    this.shareholdersStore.buildSociosTableConfig();
  }

  /** True when editing a saved operation: its capitalSocial snapshot is already the result. */
  private capitalSnapshotHydrated = false;
  /** Series before this operation; keeps series removed by a fecha de baja selectable/visible. */
  readonly capitalBaselineSeries = signal<SerieData[]>([]);

  /** Series options for the reducción step: current series plus those removed by this operation. */
  readonly reduccionSeriesOptions = computed(() => {
    // Se decide con las acciones que tenía la serie antes de la operación: una amortización que la deja a 0 no debe quitarla de su propio desplegable.
    const base = this.capitalBaselineSeries();
    const store = this.capitalStore.series();
    const enBase = new Set(base.map(s => s.id));
    const label = (s: SerieData) => ({ label: serieLabel(store.find(x => x.id === s.id) ?? s), value: s.id });
    const elegibles = [
      ...base.filter(serieTieneAcciones).map(label),
      ...store.filter(s => !enBase.has(s.id) && serieTieneAcciones(s)).map(label),
    ];
    return [...elegibles, { label: '+ Crear nueva serie', value: '__new__' as any }];
  });

  private parseEsNumber(value: unknown): number | null {
    if (value == null || value === '') return null;
    let raw = String(value).replace(/[^\d,.\-]/g, '');
    if (raw.includes(',')) raw = raw.replace(/\./g, '').replace(',', '.');
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }

  private rangosCount(rangos: unknown): number {
    if (!Array.isArray(rangos)) return 0;
    return rangos.reduce((sum, r) => {
      const desde = this.parseEsNumber(r?.desde);
      const hasta = this.parseEsNumber(r?.hasta);
      return desde != null && hasta != null && hasta >= desde ? sum + (hasta - desde + 1) : sum;
    }, 0);
  }

  /**
   * Applies the ampliación / reducción entered in the previous steps onto the capital store,
   * so the Capital Social step already shows the resulting shares and nominal values.
   */
  private applyShareChangesToCapital(): void {
    if (this.capitalSnapshotHydrated) return;
    const applyAmpliacion = this.isAmpliacion || this.isAcordeon || this.isFusionAbsorbente;
    const applyReduccion = this.isReduccion || this.isAcordeon || this.isEscision || this.isContrasplit;
    if (!applyAmpliacion && !applyReduccion) return;

    if (!this.capitalBaseline) {
      this.capitalBaseline = {
        hasSeries: this.capitalStore.hasSeries(),
        series: this.capitalStore.series().map(s => ({ ...s })),
        form: this.capitalStore.capitalSocialForm.getRawValue(),
      };
      this.capitalBaselineSeries.set(this.capitalBaseline.series);
      this.sociosBaseline ??= this.shareholdersStore.socios().map(sc => ({
        ...sc,
        shareNumberings: sc.shareNumberings?.map(n => ({ ...n })),
      }));
    }
    const base = this.capitalBaseline;
    // Series creadas después de fijar la base (desde el modal de "Crear nueva serie") se conservan al recalcular.
    if (base.hasSeries) {
      const conocidas = new Set(base.series.map(s => s.id));
      for (const s of this.capitalStore.series()) if (!conocidas.has(s.id) && s.id !== this.idsSerieNueva?.nueva) base.series.push({ ...s });
    }
    const removedSeriesIds = new Set<number>();
    const amortizados: AmortizacionRango[] = [];

    type Unit = { id: number | null; totalShares: number | null; nominalValue: number | null; sharesNumberTo: number | null; sharesNumberFrom: number | null; name?: string; feLocalId?: number };
    const units: Unit[] = base.hasSeries
      ? base.series.map(s => ({ ...s }))
      : [{
          id: null,
          totalShares: base.form['totalShares'] as number | null,
          nominalValue: base.form['nominalValue'] as number | null,
          sharesNumberFrom: base.form['sharesNumberFrom'] as number | null,
          sharesNumberTo: base.form['sharesNumberTo'] as number | null,
        }];

    // Serie nueva creada en el paso de ampliación: si el capital no tenía series, el capital único pasa a ser la primera serie (sin nombre).
    const datosAmp = this.ampDatosForm.getRawValue();
    const crearSerie = applyAmpliacion && datosAmp.modoAmpliacion === 'ampliacion_acciones' && datosAmp.accSerie === '__new__';
    const convertir = crearSerie && !base.hasSeries;
    let unidadNueva: Unit | undefined;
    if (crearSerie) {
      const ids = this.idsParaSerieNueva();
      if (convertir) {
        const previa = this.capitalStore.series().find(s => s.id === ids.primera);
        units[0] = { ...units[0], id: ids.primera, feLocalId: ids.primera, name: previa?.name ?? '' };
      }
      unidadNueva = {
        id: ids.nueva, feLocalId: ids.nueva, name: String(datosAmp.accNuevaSerieNombre ?? '').trim(),
        totalShares: 0, nominalValue: this.parseEsNumber(datosAmp.accNuevaSerieVN),
        sharesNumberFrom: this.parseEsNumber(datosAmp.accDesde), sharesNumberTo: this.parseEsNumber(datosAmp.accHasta),
      };
      units.push(unidadNueva);
    }
    const conSeries = base.hasSeries || convertir;
    const find = (serieId: unknown): Unit | undefined =>
      serieId === '__new__' && unidadNueva
        ? unidadNueva
        : conSeries
          ? units.find(u => serieId !== '' && serieId != null && u.id === Number(serieId))
          : units[0];

    if (applyAmpliacion) {
      const d = this.ampDatosForm.getRawValue();
      if (d.modoAmpliacion === 'ampliacion_vn') {
        const filas = [
          { serie: d.vnSerie, importe: d.vnImporteAmpliado, nuevoVN: d.vnNuevoVN },
          ...(d.vnExtras as Array<{ serie: unknown; importe: unknown; nuevoVN: unknown }>),
        ];
        for (const fila of filas) {
          const u = find(fila.serie);
          const nuevo = this.parseEsNumber(fila.nuevoVN);
          const importe = this.parseEsNumber(fila.importe);
          if (u && nuevo != null) u.nominalValue = nuevo;
          else if (u && importe != null) u.nominalValue = (u.nominalValue ?? 0) + importe;
        }
      } else if (d.modoAmpliacion === 'ampliacion_acciones') {
        const filas = [
          { serie: d.accSerie, nuevas: d.accNuevasAcciones, desde: d.accDesde, hasta: d.accHasta },
          ...(d.accExtras as Array<{ serie: unknown; nuevas: unknown; desde: unknown; hasta: unknown }>),
        ];
        for (const fila of filas) {
          const u = find(fila.serie);
          const n = this.parseEsNumber(fila.nuevas);
          if (u && n != null) {
            u.totalShares = (u.totalShares ?? 0) + n;
            const hasta = this.parseEsNumber(fila.hasta);
            const desde = this.parseEsNumber(fila.desde);
            u.sharesNumberTo = hasta ?? (u.sharesNumberTo != null ? u.sharesNumberTo + n : null);
            if (u.sharesNumberFrom == null && desde != null) u.sharesNumberFrom = desde;
          }
        }
      }
    }

    if (applyReduccion) {
      for (const entry of this.reduccionEntries.getRawValue() as Array<Record<string, any>>) {
        const u = find(entry['serie']);
        if (!u) continue;
        if (entry['tipoReduccion'] === 'disminucion_vn') {
          const nuevo = this.parseEsNumber(entry['nuevoVN']);
          const importe = this.parseEsNumber(entry['importeReducido']);
          if (nuevo != null) u.nominalValue = nuevo;
          else if (importe != null) u.nominalValue = this.nominalTrasImporte(u.nominalValue ?? 0, importe);
        } else if (entry['tipoReduccion'] === 'amortizacion_acciones') {
          const rangos = this.amortizacionRangos(entry);
          const wholeSerie = this.bajaDeSerie(entry, base.hasSeries && u.id != null, u.totalShares);
          const n = wholeSerie
            ? (u.totalShares ?? 0)
            : (rangos.reduce((sum, r) => sum + (r.hasta - r.desde + 1), 0)
              || this.parseEsNumber(entry['numAccionesAmortizadas']) || null);
          if (n != null) {
            u.totalShares = Math.max((u.totalShares ?? 0) - n, 0);
            // Only a range touching an end of the serie moves its numbering; a middle one leaves a gap.
            for (const r of rangos) {
              if (u.sharesNumberFrom != null && u.sharesNumberTo != null) {
                if (r.desde <= u.sharesNumberFrom && r.hasta >= u.sharesNumberFrom) u.sharesNumberFrom = r.hasta + 1;
                else if (r.hasta >= u.sharesNumberTo && r.desde <= u.sharesNumberTo) u.sharesNumberTo = r.desde - 1;
              }
              amortizados.push({ seriesId: base.hasSeries ? u.id : null, desde: r.desde, hasta: r.hasta });
            }
            if (rangos.length === 0 && !wholeSerie && u.sharesNumberTo != null) {
              u.sharesNumberTo = Math.max(u.sharesNumberTo - n, u.sharesNumberFrom ?? 0);
            }
          }
          if (wholeSerie) removedSeriesIds.add(u.id!);
          // Una serie sin acciones tras amortizar desaparece del capital, tenga o no fecha de baja.
          else if (u.id != null && (u.totalShares ?? 0) === 0) removedSeriesIds.add(u.id);
        }
      }
    }

    if (conSeries) {
      if (!this.capitalStore.hasSeries()) {
        this.aplicandoConversion = true;
        this.capitalStore.capitalSocialForm.patchValue({ hasSeries: true });
        this.aplicandoConversion = false;
      }
      this.capitalStore.series.set((units as SerieData[]).filter(u => !removedSeriesIds.has(u.id)));
      this.capitalStore.buildSeriesTableConfig();
      if (convertir) this.asignarSociosSinSerie(units[0].id!, units[0].name ?? '');
    } else {
      const { id: _id, ...single } = units[0];
      this.capitalStore.capitalSocialForm.patchValue(single);
    }
    if (applyReduccion && this.sociosBaseline) {
      this.shareholdersStore.socios.set(applyAmortizacion(this.sociosBaseline, amortizados, removedSeriesIds));
      this.shareholdersStore.buildSociosTableConfig();
    }
    this.syncSociosWithCapital();
  }

  /** La fecha de baja elimina la serie solo si no se indican rangos o estos la cubren entera; un tramo suelto la deja con menos acciones. */
  private bajaDeSerie(entry: Record<string, any>, hasSeries: boolean, totalShares?: number | null): boolean {
    if (!entry['fechaBaja'] || !hasSeries) return false;
    const rangos = this.amortizacionRangos(entry);
    if (rangos.length === 0) return true;
    return totalShares != null && rangos.reduce((sum, r) => sum + (r.hasta - r.desde + 1), 0) >= totalShares;
  }

  /** Valid numbering ranges of an amortización entry, with the series the capital store uses. */
  private amortizacionRangos(entry: Record<string, any>): Array<{ desde: number; hasta: number }> {
    if (!Array.isArray(entry['rangos'])) return [];
    return (entry['rangos'] as Array<Record<string, unknown>>)
      .map(r => ({ desde: this.parseEsNumber(r['desde']), hasta: this.parseEsNumber(r['hasta']) }))
      .filter((r): r is { desde: number; hasta: number } => r.desde != null && r.hasta != null && r.hasta >= r.desde);
  }

  /** Shares amortized by an entry, derived from its ranges (shown read-only in the step). */
  amortizacionCount(entryIndex: number): number {
    return this.rangosCount(this.getReduccionRangos(entryIndex).getRawValue());
  }

  /** Errors that block the reducción step: amortizaciones without ranges, out of the serie or overlapping. */
  get reduccionStepErrors(): string[] {
    const errors: string[] = [];
    const entries = this.reduccionEntries.getRawValue() as Array<Record<string, any>>;
    // Before the capital step runs there is no baseline yet: the store still holds the pre-operation capital.
    const hasSeries = this.capitalBaseline?.hasSeries ?? this.capitalStore.hasSeries();
    const baseSeries = this.capitalBaseline?.series ?? this.capitalStore.series();
    const baseForm = this.capitalBaseline?.form ?? this.capitalStore.capitalSocialForm.getRawValue();
    entries.forEach((entry, i) => {
      // Una serie sin acciones antes de la operación no es elegible (p. ej. al editar una operación con la serie ya vacía).
      const elegida = hasSeries && !this.capitalSnapshotHydrated && entry['serie'] ? baseSeries.find(s => s.id === Number(entry['serie'])) : undefined;
      if (elegida && !serieTieneAcciones(elegida)) errors.push(`Reducción ${i + 1}: la serie ${elegida.name} no tiene acciones.`);
      if (entry['tipoReduccion'] !== 'amortizacion_acciones') return;
      const label = `Reducción ${i + 1}`;
      const rangos = this.amortizacionRangos(entry);
      if (this.bajaDeSerie(entry, hasSeries)) return;
      if (rangos.length === 0) {
        errors.push(`${label}: indica la numeración de las acciones amortizadas.`);
        return;
      }
      const sorted = [...rangos].sort((a, b) => a.desde - b.desde);
      if (sorted.some((r, k) => k > 0 && r.desde <= sorted[k - 1].hasta)) {
        errors.push(`${label}: los rangos de numeración se solapan.`);
      }
      const bounds = hasSeries
        ? baseSeries.find(s => s.id === Number(entry['serie']))
        : { sharesNumberFrom: baseForm['sharesNumberFrom'] as number | null, sharesNumberTo: baseForm['sharesNumberTo'] as number | null };
      // Al editar, el capital cargado ya es el resultado de la operación: no hay numeración previa con la que comparar.
      if (!this.capitalSnapshotHydrated && bounds?.sharesNumberFrom != null && bounds.sharesNumberTo != null
        && sorted.some(r => r.desde < bounds.sharesNumberFrom! || r.hasta > bounds.sharesNumberTo!)) {
        errors.push(`${label}: hay rangos fuera de la numeración (${this.fmt(bounds.sharesNumberFrom)}–${this.fmt(bounds.sharesNumberTo)}).`);
      }
    });

    // Entradas que se contradicen entre sí: se agrupan por serie (o por el capital único si no hay series).
    const grupos = new Map<string, number[]>();
    entries.forEach((entry, i) => {
      if (!entry['tipoReduccion']) return;
      const key = hasSeries ? String(entry['serie'] ?? '') : 'unica';
      if (!key) return;
      grupos.set(key, [...(grupos.get(key) ?? []), i]);
    });
    const etiqueta = (i: number) => `Reducción ${i + 1}`;
    for (const indices of grupos.values()) {
      if (indices.length < 2) continue;

      const baja = indices.find(i => entries[i]['tipoReduccion'] === 'amortizacion_acciones' && this.bajaDeSerie(entries[i], hasSeries));
      if (baja != null) {
        errors.push(`${indices.map(etiqueta).join(' y ')} afectan a la misma serie, pero la serie se da de baja por completo en ${etiqueta(baja)}.`);
      }

      const vn = indices.filter(i => entries[i]['tipoReduccion'] === 'disminucion_vn');
      if (vn.length > 1) {
        errors.push(`Hay más de una disminución de VN sobre la misma serie (${vn.map(etiqueta).join(' y ')}).`);
      }

      const rangos = indices
        .filter(i => entries[i]['tipoReduccion'] === 'amortizacion_acciones' && !this.bajaDeSerie(entries[i], hasSeries))
        .flatMap(i => this.amortizacionRangos(entries[i]).map(r => ({ i, ...r })))
        .sort((a, b) => a.desde - b.desde);
      for (let k = 1; k < rangos.length; k++) {
        const previo = rangos.slice(0, k).find(r => r.i !== rangos[k].i && rangos[k].desde <= r.hasta);
        if (previo) {
          const msg = `${etiqueta(Math.min(previo.i, rangos[k].i))} y ${etiqueta(Math.max(previo.i, rangos[k].i))}: sus rangos de numeración se solapan.`;
          if (!errors.includes(msg)) errors.push(msg);
        }
      }
    }
    return errors;
  }

  // ── Validaciones por paso: bloquean "Siguiente" y se muestran en un aviso ───────────────────
  readonly showStepErrors = signal(false);

  private get escrituraErrors(): string[] {
    return String(this.metaForm.get('fechaEscritura')?.value ?? '').trim() ? [] : ['La fecha de escritura es obligatoria.'];
  }

  /** Ampliación de acciones (también dentro de acordeón y fusión con ampliación). */
  get ampliacionStepErrors(): string[] {
    const d = this.ampDatosForm.getRawValue();
    if (d.modoAmpliacion !== 'ampliacion_acciones' && d.modoAmpliacion !== 'ampliacion_vn') return [];
    if (this.isFusionAbsorbente && this.selectedTipoFusion() !== 'con_ampliacion') return [];

    // Una serie sin acciones no es elegible (p. ej. al editar una operación con la serie ya vacía).
    const sinAcciones = (series: unknown[]): string[] => this.capitalSnapshotHydrated ? [] : series
      .map(id => (id ? this.capitalStore.series().find(s => s.id === Number(id)) : undefined))
      .filter((s): s is SerieData => !!s && !serieTieneAcciones(s))
      .map(s => `La serie ${s.name} no tiene acciones.`);
    if (d.modoAmpliacion === 'ampliacion_vn') {
      return sinAcciones([d.vnSerie, ...(d.vnExtras as Array<{ serie: unknown }>).map(r => r.serie)]);
    }

    const errors: string[] = sinAcciones([d.accSerie, ...(d.accExtras as Array<{ serie: unknown }>).map(r => r.serie)]);
    if (d.accSerie === '__new__' && !String(d.accNuevaSerieNombre ?? '').trim()) errors.push('Indica el nombre de la nueva serie.');
    const filas = [
      { serie: d.accSerie, nuevas: d.accNuevasAcciones, desde: d.accDesde, hasta: d.accHasta },
      ...(d.accExtras as Array<{ serie: unknown; nuevas: unknown; desde: unknown; hasta: unknown }>),
    ];
    filas.forEach((fila, i) => {
      const prefijo = i === 0 ? '' : `Serie ${i + 1}: `;
      const nuevas = this.parseEsNumber(fila.nuevas);
      const desde = this.parseEsNumber(fila.desde);
      const hasta = this.parseEsNumber(fila.hasta);

      if (desde != null && hasta != null) {
        if (hasta < desde) {
          errors.push(`${prefijo}El rango "De la / A la" no es válido: el fin es menor que el inicio.`);
        } else {
          const numeros = hasta - desde + 1;
          if (nuevas != null && numeros !== nuevas) {
            errors.push(`${prefijo}Las nuevas acciones (${this.fmt(nuevas)}) no coinciden con el rango ${this.fmt(desde)}–${this.fmt(hasta)} (${this.fmt(numeros)} números).`);
          }
        }
      }

      // Una serie nueva tiene su propia numeración: no se compara con la última acción de otra serie.
      if (desde != null && !this.capitalSnapshotHydrated && fila.serie !== '__new__') {
        const hasSeries = this.capitalBaseline?.hasSeries ?? this.capitalStore.hasSeries();
        const series = this.capitalBaseline?.series ?? this.capitalStore.series();
        const form = this.capitalBaseline?.form ?? this.capitalStore.capitalSocialForm.getRawValue();
        const fin = hasSeries
          ? series.find(s => s.id === Number(fila.serie))?.sharesNumberTo
          : (form['sharesNumberTo'] as number | null);
        if (fin != null && desde <= fin) {
          errors.push(`${prefijo}La numeración nueva debe empezar después de la última acción de la serie (${this.fmt(fin)}).`);
        }
      }
    });
    return errors;
  }

  /** Todas las acciones del capital deben estar asignadas a algún socio (si ya hay socios). */
  get sociosStepErrors(): string[] {
    const socios = this.shareholdersStore.socios();
    // Σ acciones × % de titularidad; el usufructo no cuenta (las acciones ya están en la plena/nuda propiedad).
    const asignadas = socios.reduce((total, s) => {
      const numeraciones = s.shareNumberings ?? [];
      if (numeraciones.length === 0) return total + (s.sharesCount ?? 0);
      return total + numeraciones
        .filter(n => (n.titulo ?? '').trim().toLowerCase() !== 'usufructo')
        .reduce((sum, n) => sum + (n.sharesCount ?? 0) * (Number(n.ownershipPercentage ?? 0) / 100), 0);
    }, 0);
    if (socios.length === 0 || asignadas === 0) return [];

    const capital = this.capitalStore.hasSeries()
      ? this.capitalStore.series().reduce((sum, s) => sum + (s.totalShares ?? 0), 0)
      : Number(this.capitalStore.capitalSocialForm.getRawValue().totalShares) || 0;
    const fmt = (n: number) => n.toLocaleString('de-DE', { maximumFractionDigits: 4 });
    if (Math.abs(asignadas - capital) < 0.00005) return [];
    if (asignadas < capital) {
      return [`Hay ${fmt(capital - asignadas)} acciones sin asignar a ningún socio (capital: ${fmt(capital)}, socios: ${fmt(asignadas)}). Asígnalas antes de continuar.`];
    }
    if (asignadas > capital) {
      return [`Los socios tienen ${fmt(asignadas - capital)} acciones de más (capital: ${fmt(capital)}, socios: ${fmt(asignadas)}). Corrígelo antes de continuar.`];
    }
    return [];
  }

  /** Una serie sin nombre (la primera, al convertir un capital único) no se puede dejar así: se descartaría al guardar. */
  get capitalStepErrors(): string[] {
    if (!this.capitalStore.hasSeries()) return [];
    return this.capitalStore.series()
      .map((s, i) => (s.name?.trim() ? '' : `La serie ${i + 1} no tiene nombre: edítala y ponle nombre.`))
      .filter(Boolean);
  }

  /** Errores que bloquean el paso actual. */
  get stepErrors(): string[] {
    switch (this.currentStepKey()) {
      case 'escritura_meta': return this.escrituraErrors;
      case 'step_reduccion_tipos': return this.reduccionStepErrors;
      case 'step_ampliacion_datos':
      case 'step_fusion': return this.ampliacionStepErrors;
      case 'capitalSocial': return this.capitalStepErrors;
      case 'socios': return this.sociosStepErrors;
      default: return [];
    }
  }

  private syncIdentificationOperationCache(): void {
    const data = this.identificationStep?.serializeForCorporateOperation();
    if (data) {
      this.identificationOperationDataCache = data;
    }
  }

  private getIdentificationOperationData(): Record<string, unknown> {
    if (this.identificationStep) {
      this.identificationOperationDataCache =
        this.identificationStep.serializeForCorporateOperation();
    }
    return this.identificationOperationDataCache ?? {};
  }

  onIdentificationPayload(data: Record<string, unknown>): void {
    this.identificationOperationDataCache = data;
  }

  /** Pre-populates all wizard forms from an existing operation (edit mode). */
  private populateForEdit(op: CorporateOperation): void {
    const data = op.operationData ?? {};

    // 1. Meta form (deed + registry data)
    this.metaForm.patchValue({
      fechaEscritura: isoToDisplay(op.deedDate),
      conceptoEscritura: op.deedConcept ?? '',
      notario: op.notary ?? '',
      protocolo: op.protocol ?? '',
      irus: op.irus ?? '',
      observaciones: op.observations ?? '',
      provinciaRM: op.mercantileRegistryProvince ?? '',
      fechaInscripcion: isoToDisplay(op.registrationDate),
      tomo: op.tome ?? '',
      folio: op.folio ?? '',
      hoja: op.sheet ?? '',
      seccion: op.section ?? '',
      libro: op.book ?? '',
      numInscripcion: op.inscriptionNumber ?? '',
    });

    // 2. Cross-tab forms
    switch (op.subtipo) {
      case 'denominacion_social': {
        const previous = String(data['previousName'] ?? '').trim();
        const next = String(data['newName'] ?? '').trim();
        this.denominacionPreviousName = previous;
        this.crossTabForm.patchValue({ nuevaRazonSocial: next || previous });
        break;
      }
      case 'domicilio_social':
        this.crossTabForm.patchValue({
          tipo: data['tipo'] ?? 'SOCIAL',
          fechaAlta: isoToDisplay(data['fechaAlta']),
          siglas: data['siglas'] ?? '',
          viapublica: data['viapublica'] ?? '',
          numero: data['numero'] ?? '',
          escalera: data['escalera'] ?? '',
          piso: data['piso'] ?? '',
          puerta: data['puerta'] ?? '',
          municipio: data['municipio'] ?? '',
          cp: data['cp'] ?? '',
          provincia: data['provincia'] ?? '',
          pais: data['pais'] ?? 'ES',
          refCatastral: data['refCatastral'] ?? '',
          observaciones: data['observaciones'] ?? '',
        });
        break;
      case 'articulado_estatutos':
        this.crossTabForm.patchValue({
          articulosModificados: data['articulosModificados'] ?? '',
          descripcionCambio: data['descripcionCambio'] ?? '',
        });
        break;
    }

    // 3. Ampliación forms
    if (data['tipoAmpliacion']) {
      this.ampTipoForm.patchValue({
        tipoAmpliacion: data['tipoAmpliacion'] ?? '',
        detalleAportaciones: data['detalleAportaciones'] ?? '',
        detalleCanje: data['detalleCanje'] ?? '',
      });
      this.selectedTipoAmpliacion.set(data['tipoAmpliacion'] ?? '');
    }
    if (data['modoAmpliacion']) {
      this.ampDatosForm.patchValue({
        modoAmpliacion: data['modoAmpliacion'] ?? '',
        vnSerie: data['vnSerie'] ?? '',
        vnImporteAmpliado: data['vnImporteAmpliado'] ?? '',
        vnNuevoVN: data['vnNuevoVN'] ?? '',
        accSerie: data['accSerie'] ?? '',
        accNumAmpliado: data['accNumAmpliado'] ?? data['accNuevasAcciones'] ?? '',
        accNuevasAcciones: data['accNuevasAcciones'] ?? data['accNumAmpliado'] ?? '',
        accDesde: data['accDesde'] ?? '',
        accHasta: data['accHasta'] ?? '',
        accNuevaSerieNombre: data['accNuevaSerie']?.nombre ?? '',
        accNuevaSerieVN: data['accNuevaSerie']?.vn ?? '',
      });
      this.setVnExtras(data['vnExtras']);
      this.setAccExtras(data['accExtras']);
      this.selectedModoAmpliacion.set(data['modoAmpliacion'] ?? '');
    }

    // 4. Reducción entries (contrasplit: single entry; reducción/escisión: array)
    if (data['contrasplit']) {
      const entry = data['contrasplit'];
      this.clearReduccionEntries();
      const group = this.createReduccionEntry();
      this.patchReduccionEntryGroup(group, {
        tipoReduccion: entry.tipoContrasplit,
        serie: entry.serie,
        importeReducido: entry.importeAmpliado,
        nuevoVN: entry.nuevoVN,
        numAccionesAmortizadas: entry.numAccionesAmortizadas,
        rangos: entry.rangos,
      });
      this.reduccionEntries.push(group);
    } else if (Array.isArray(data['reducciones']) && data['reducciones'].length > 0) {
      this.clearReduccionEntries();
      for (const entry of data['reducciones']) {
        const group = this.createReduccionEntry();
        this.patchReduccionEntryGroup(group, entry);
        this.reduccionEntries.push(group);
      }
    } else if (data['reduccion']) {
      this.clearReduccionEntries();
      const group = this.createReduccionEntry();
      this.patchReduccionEntryGroup(group, data['reduccion']);
      this.reduccionEntries.push(group);
    }

    // 5. Transmisión form
    if (data['tipoTransmision'] !== undefined || data['transmitente'] !== undefined) {
      this.transmisionForm.patchValue({
        tipoTransmision: data['tipoTransmision'] ?? '',
        transmitente: data['transmitente'] ?? null,
      });
      const savedAdquirentes: Array<Record<string, any>> = Array.isArray(data['adquirentes']) && data['adquirentes'].length > 0
        ? data['adquirentes']
        : [{
            adquirente: data['adquirente'],
            numAccionesVendidas: data['numAccionesVendidas'],
            numeracionAccionesVendidas: data['numeracionAccionesVendidas'],
            primaEmision: data['primaEmision'],
            precioTransmision: data['precioTransmision'],
          }];
      this.adquirentes.clear();
      for (const a of savedAdquirentes) {
        const group = this.createAdquirente();
        group.patchValue({
          adquirente: a['adquirente'] ?? null,
          numAccionesVendidas: a['numAccionesVendidas'] ?? '',
          primaEmision: a['primaEmision'] ?? '',
          precioTransmision: a['precioTransmision'] ?? '',
        });
        const rangos = group.get('rangos') as FormArray;
        if (Array.isArray(a['numeracionAccionesVendidas']) && a['numeracionAccionesVendidas'].length > 0) {
          rangos.clear();
          for (const r of a['numeracionAccionesVendidas']) {
            const rg = this.createTransmisionRango();
            rg.patchValue({ serie: r.serie ?? null, desde: r.desde ?? '', hasta: r.hasta ?? '' });
            rangos.push(rg);
          }
        }
        this.adquirentes.push(group);
      }
      if (Array.isArray(data['bajasParticipaciones'])) {
        this.bajasForm.clear();
        for (const b of data['bajasParticipaciones']) {
          this.bajasForm.push(this.createBajaRow({
            seriesId: b.seriesId ?? null,
            serie: b.serieNombre ?? '—',
            titulo: b.titulo ?? '',
            desde: b.desde,
            hasta: b.hasta,
            count: b.count ?? (b.hasta - b.desde + 1),
            fechaBaja: isoToDisplay(b.fechaBaja),
          }));
        }
      }
    }

    // 6. Fusión form
    if (data['tipoFusion']) {
      this.fusionForm.patchValue({ tipoFusion: data['tipoFusion'] ?? '' });
      this.selectedTipoFusion.set(data['tipoFusion'] ?? '');
      // ampliacion inside fusion
      if (data['ampliacion']) {
        const amp = data['ampliacion'];
        this.ampTipoForm.patchValue({
          tipoAmpliacion: amp.tipoAmpliacion ?? '',
          detalleAportaciones: amp.detalleAportaciones ?? '',
          detalleCanje: amp.detalleCanje ?? '',
        });
        this.ampDatosForm.patchValue({
          modoAmpliacion: amp.modoAmpliacion ?? '',
          vnSerie: amp.vnSerie ?? '',
          vnImporteAmpliado: amp.vnImporteAmpliado ?? '',
          vnNuevoVN: amp.vnNuevoVN ?? '',
          accSerie: amp.accSerie ?? '',
          accNumAmpliado: amp.accNumAmpliado ?? amp.accNuevasAcciones ?? '',
          accNuevasAcciones: amp.accNuevasAcciones ?? amp.accNumAmpliado ?? '',
          accDesde: amp.accDesde ?? '',
          accHasta: amp.accHasta ?? '',
          accNuevaSerieNombre: amp.accNuevaSerie?.nombre ?? '',
          accNuevaSerieVN: amp.accNuevaSerie?.vn ?? '',
        });
        this.setVnExtras(amp.vnExtras);
        this.setAccExtras(amp.accExtras);
      }
    }

    // 7. Transformación / identificación (embedded form)
    if (this.subtipo === 'cambio_forma_juridica') {
      this.identificationOperationOverlay = { ...data };
      this.identificationOperationDataCache = { ...data };
    }

    const capitalAffectingSubtipos = new Set([
      'modificacion_serie', 'operacion_acordeon', 'fusion_absorbente', 'contrasplit',
      'ampliacion_capital', 'reduccion_capital', 'escision',
      'compraventa_donacion_herencia', 'fusion_socios_filial',
    ]);
    if (op.categoria === 'constitucion' || (op.subtipo && capitalAffectingSubtipos.has(op.subtipo))) {
      if (data['capitalSocial']) {
        this.capitalStore.hydrateFromOperation(data['capitalSocial']);
        this.capitalSnapshotHydrated = true;
      }
    }
    if (Array.isArray(data['socios']) && data['socios'].length > 0) {
      this.shareholdersStore.hydrateFromOperation(data['socios'], this.capitalStore);
      this.sociosSnapshotHydrated = true;
    }
    if (data['administracion']) {
      this.administrationStore.hydrateFromOperation(data['administracion']);
    }
    if (Array.isArray(data['apoderados']) && data['apoderados'].length > 0) {
      this.attorniesStore.hydrateFromOperation(data['apoderados']);
    }
  }

  onTipoAmpliacionChange(value: string): void {
    this.selectedTipoAmpliacion.set(value);
  }

  onModoAmpliacionChange(value: string): void {
    this.selectedModoAmpliacion.set(value);
  }

  onTipoFusionChange(value: string): void {
    this.selectedTipoFusion.set(value);
  }

  onSerieChange(controlName: 'vnSerie' | 'accSerie', value: any): void {
    if (value === '__new__') {
      this.ampDatosForm.get(controlName)?.setValue(null);
      this.capitalStore.openFormModal();
    } else if (controlName === 'vnSerie') {
      this.recalcNuevoVnAmpliacion();
    }
  }

  /** Nominal value of a serie before this operation's changes (baseline once applied to capital). */
  private currentNominalValue(serieId: unknown): number | null {
    const base = this.capitalBaseline;
    if (base && !base.hasSeries) return (base.form['nominalValue'] as number | null) ?? null;
    if (!base && !this.capitalStore.hasSeries()) {
      return this.capitalStore.capitalSocialForm.getRawValue().nominalValue ?? null;
    }
    if (serieId === '' || serieId == null) return null;
    const list = base ? base.series : this.capitalStore.series();
    return list.find(s => s.id === Number(serieId))?.nominalValue ?? null;
  }

  private formatNominal(value: number): string {
    return `${value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 4 })} €`;
  }

  recalcNuevoVnAmpliacion(): void {
    const d = this.ampDatosForm.getRawValue();
    const actual = this.currentNominalValue(d.vnSerie);
    const importe = this.parseEsNumber(d.vnImporteAmpliado);
    if (actual == null || importe == null) return;
    this.ampDatosForm.patchValue({ vnNuevoVN: this.formatNominal(actual + importe) });
  }

  get vnExtras(): FormArray {
    return this.ampDatosForm.get('vnExtras') as FormArray;
  }

  addVnExtra(): void {
    this.vnExtras.push(this.fb.nonNullable.group({ serie: [''], importe: [''], nuevoVN: [''] }));
  }

  removeVnExtra(index: number): void {
    this.vnExtras.removeAt(index);
  }

  private setVnExtras(rows: unknown): void {
    this.vnExtras.clear();
    for (const r of Array.isArray(rows) ? rows : []) {
      this.addVnExtra();
      this.vnExtras.at(this.vnExtras.length - 1).patchValue({ serie: r.serie ?? '', importe: r.importe ?? '', nuevoVN: r.nuevoVN ?? '' });
    }
  }

  get accExtras(): FormArray {
    return this.ampDatosForm.get('accExtras') as FormArray;
  }

  addAccExtra(): void {
    this.accExtras.push(this.fb.nonNullable.group({ serie: [''], nuevas: [''], desde: [''], hasta: [''] }));
  }

  removeAccExtra(index: number): void {
    this.accExtras.removeAt(index);
  }

  private setAccExtras(rows: unknown): void {
    this.accExtras.clear();
    for (const r of Array.isArray(rows) ? rows : []) {
      this.addAccExtra();
      this.accExtras.at(this.accExtras.length - 1).patchValue({ serie: r.serie ?? '', nuevas: r.nuevas ?? '', desde: r.desde ?? '', hasta: r.hasta ?? '' });
    }
  }

  onAccExtraSerieChange(index: number, value: any): void {
    if (value === '__new__') {
      this.accExtras.at(index).get('serie')?.setValue(null);
      this.capitalStore.openFormModal();
    }
  }

  /** `serieId` permite usar la serie del evento de cambio, antes de que el control esté actualizado. */
  recalcNuevoVnExtra(index: number, serieId?: unknown): void {
    const group = this.vnExtras.at(index);
    const actual = this.currentNominalValue(serieId ?? group.get('serie')?.value);
    const importe = this.parseEsNumber(group.get('importe')?.value);
    if (actual == null || importe == null) return;
    group.patchValue({ nuevoVN: this.formatNominal(actual + importe) });
  }

  onVnExtraSerieChange(index: number, value: any): void {
    if (value === '__new__') {
      this.vnExtras.at(index).get('serie')?.setValue(null);
      this.capitalStore.openFormModal();
    } else {
      this.recalcNuevoVnExtra(index, value);
    }
  }

  /** Nuevo VN de la entrada: una reducción resta el importe; el contrasplit "Ampliación VN" lo suma. */
  private nominalTrasImporte(actual: number, importe: number): number {
    return this.isContrasplit ? actual + importe : Math.max(actual - importe, 0);
  }

  /** `serieId` permite usar la serie del evento de cambio, antes de que el control esté actualizado. */
  recalcNuevoVnReduccion(entryIndex: number, serieId?: unknown): void {
    const group = this.reduccionEntries.at(entryIndex) as FormGroup;
    const actual = this.currentNominalValue(serieId ?? group.get('serie')?.value);
    const importe = this.parseEsNumber(group.get('importeReducido')?.value);
    if (actual == null || importe == null) return;
    group.patchValue({ nuevoVN: this.formatNominal(this.nominalTrasImporte(actual, importe)) });
  }

  // ── Reducción helpers ───────────────────────────────────────────────────
  createReduccionEntry(): FormGroup {
    return this.fb.group({
      tipoReduccion: [''],
      serie: [''],
      // Disminución VN
      importeReducido: [''],
      nuevoVN: [''],
      // Amortización acciones
      numAccionesAmortizadas: [''],
      fechaBaja: [''],
      rangos: this.fb.array([this.createReduccionRango()]),
    });
  }

  createReduccionRango(): FormGroup {
    return this.fb.group({
      desde: [''],
      hasta: [''],
    });
  }

  getReduccionRangos(entryIndex: number): FormArray {
    return this.reduccionEntries.at(entryIndex).get('rangos') as FormArray;
  }

  addReduccionRango(entryIndex: number): void {
    this.getReduccionRangos(entryIndex).push(this.createReduccionRango());
  }

  removeReduccionRango(entryIndex: number, rangoIndex: number): void {
    const rangos = this.getReduccionRangos(entryIndex);
    if (rangos.length > 1) {
      rangos.removeAt(rangoIndex);
    }
  }

  onReduccionSerieChange(entryIndex: number, value: any): void {
    if (value === '__new__') {
      (this.reduccionEntries.at(entryIndex) as FormGroup).get('serie')?.setValue(null);
      this.capitalStore.openFormModal();
    } else {
      this.recalcNuevoVnReduccion(entryIndex, value);
    }
  }

  addReduccionEntry(): void {
    this.reduccionEntries.push(this.createReduccionEntry());
  }

  removeReduccionEntry(index: number): void {
    if (this.reduccionEntries.length > 1) {
      this.reduccionEntries.removeAt(index);
    }
  }

  serializeReduccionEntries(): Record<string, unknown>[] {
    return (this.reduccionEntries.getRawValue() as Array<Record<string, unknown>>)
      .filter(entry => !this.isReduccionEntryEmpty(entry))
      .map(entry => this.serializeReduccionEntry(entry));
  }

  private clearReduccionEntries(): void {
    while (this.reduccionEntries.length > 0) {
      this.reduccionEntries.removeAt(0);
    }
  }

  private patchReduccionEntryGroup(group: FormGroup, entry: Record<string, unknown>): void {
    group.patchValue({
      tipoReduccion: entry['tipoReduccion'] ?? '',
      serie: entry['serie'] ?? '',
      importeReducido: entry['importeReducido'] ?? '',
      nuevoVN: entry['nuevoVN'] ?? '',
      numAccionesAmortizadas: entry['numAccionesAmortizadas'] ?? '',
      fechaBaja: isoToDisplay(entry['fechaBaja'] as string | undefined),
    });
    const rangos = group.get('rangos') as FormArray;
    const entryRangos = entry['rangos'];
    if (Array.isArray(entryRangos) && entryRangos.length > 0) {
      while (rangos.length > 0) rangos.removeAt(0);
      for (const r of entryRangos) {
        const rg = this.createReduccionRango();
        rg.patchValue({ desde: (r as any).desde ?? '', hasta: (r as any).hasta ?? '' });
        rangos.push(rg);
      }
    }
  }

  private isReduccionEntryEmpty(entry: Record<string, unknown>): boolean {
    const rangos = entry['rangos'];
    const hasRangos = Array.isArray(rangos)
      && rangos.some((r: any) => r?.desde || r?.hasta);
    return !entry['tipoReduccion']
      && !entry['serie']
      && !entry['importeReducido']
      && !entry['nuevoVN']
      && !entry['numAccionesAmortizadas']
      && !hasRangos;
  }

  private resolveSerieNombre(serieId: unknown): string | undefined {
    if (serieId == null || serieId === '') return undefined;
    const numId = Number(serieId);
    const match = this.capitalStore.series().find(s =>
      s.id === serieId
      || s.feLocalId === serieId
      || (!Number.isNaN(numId) && (s.id === numId || s.feLocalId === numId)),
    );
    if (match?.name) return match.name;
    // serie removed from the capital by this operation (fecha de baja): still resolvable from the baseline
    return this.capitalBaselineSeries().find(s =>
      s.id === serieId || s.feLocalId === serieId
      || (!Number.isNaN(numId) && (s.id === numId || s.feLocalId === numId)),
    )?.name || undefined;
  }

  private serializeAmpliacionDatos(): Record<string, unknown> {
    const tipo = this.ampTipoForm.getRawValue();
    const datos = this.ampDatosForm.getRawValue();
    return {
      tipoAmpliacion: tipo.tipoAmpliacion || undefined,
      detalleAportaciones: tipo.detalleAportaciones || undefined,
      detalleCanje: tipo.detalleCanje || undefined,
      modoAmpliacion: datos.modoAmpliacion || undefined,
      vnSerie: datos.vnSerie || undefined,
      vnSerieNombre: this.resolveSerieNombre(datos.vnSerie),
      vnImporteAmpliado: datos.vnImporteAmpliado || undefined,
      vnNuevoVN: datos.vnNuevoVN || undefined,
      vnExtras: (datos.vnExtras as Array<{ serie: string; importe: string; nuevoVN: string }>)
        .filter(r => r.serie)
        .map(r => ({ serie: r.serie, serieNombre: this.resolveSerieNombre(r.serie), importe: r.importe || undefined, nuevoVN: r.nuevoVN || undefined })),
      // Con serie nueva (creada aquí), accSerie apunta a su id local y accSerieNombre a su nombre.
      accSerie: (datos.accSerie === '__new__' ? this.idsSerieNueva?.nueva : datos.accSerie) || undefined,
      accSerieNombre: datos.accSerie === '__new__' ? (datos.accNuevaSerieNombre.trim() || undefined) : this.resolveSerieNombre(datos.accSerie),
      accNuevaSerie: datos.accSerie === '__new__' ? { nombre: datos.accNuevaSerieNombre.trim(), vn: datos.accNuevaSerieVN || undefined } : undefined,
      accNumAmpliado: datos.accNuevasAcciones || undefined,
      accNuevasAcciones: datos.accNuevasAcciones || undefined,
      accDesde: datos.accDesde || undefined,
      accHasta: datos.accHasta || undefined,
      accExtras: (datos.accExtras as Array<{ serie: string; nuevas: string; desde: string; hasta: string }>)
        .filter(r => r.serie)
        .map(r => ({ serie: r.serie, serieNombre: this.resolveSerieNombre(r.serie), nuevas: r.nuevas || undefined, desde: r.desde || undefined, hasta: r.hasta || undefined })),
    };
  }

  private serializeReduccionEntry(entry: Record<string, unknown>): Record<string, unknown> {
    const rangos = Array.isArray(entry['rangos'])
      ? (entry['rangos'] as Array<Record<string, unknown>>).filter(r => r['desde'] || r['hasta'])
      : [];
    return {
      tipoReduccion: entry['tipoReduccion'],
      serie: entry['serie'] || undefined,
      serieNombre: this.resolveSerieNombre(entry['serie']),
      importeReducido: entry['importeReducido'] || undefined,
      nuevoVN: entry['nuevoVN'] || undefined,
      numAccionesAmortizadas: this.rangosCount(rangos) || entry['numAccionesAmortizadas'] || undefined,
      fechaBaja: this.serializeFechaBaja(entry),
      rangos: rangos.length > 0 ? rangos : undefined,
      sociosAfectados: this.serializeSociosAfectados(entry),
    };
  }

  /** Holdings each socio loses in an amortización, so the operation detail can show only those. */
  private serializeSociosAfectados(entry: Record<string, unknown>): unknown[] | undefined {
    if (entry['tipoReduccion'] !== 'amortizacion_acciones' || !this.sociosBaseline) return undefined;
    const hasSeries = this.capitalBaseline?.hasSeries ?? false;
    const serieId = entry['serie'] === '' || entry['serie'] == null ? null : Number(entry['serie']);
    const wholeSerie = this.bajaDeSerie(entry, hasSeries && serieId != null);
    const rangos = this.amortizacionRangos(entry).map(r => ({ seriesId: hasSeries ? serieId : null, ...r }));
    const base = this.capitalBaseline;
    const nominalDe = (n: { seriesId: number | null }): number | null =>
      hasSeries
        ? (base?.series.find(s => s.id === n.seriesId)?.nominalValue ?? null)
        : ((base?.form['nominalValue'] as number | null | undefined) ?? null);
    const afectados = extractAmortizedShares(this.sociosBaseline, rangos, new Set(wholeSerie ? [serieId!] : []), nominalDe);
    return afectados.length > 0 ? afectados : undefined;
  }

  /** Fecha de baja of the serie, only meaningful for an amortización de acciones. */
  private serializeFechaBaja(entry: Record<string, unknown>): string | undefined {
    return entry['tipoReduccion'] === 'amortizacion_acciones'
      ? ddmmToISO(entry['fechaBaja'] as string | undefined)
      : undefined;
  }

  // ── Transmisión rangos helpers ───────────────────────────────────────────
  createTransmisionRango(): FormGroup {
    return this.fb.group({ serie: [null], desde: [''], hasta: [''] });
  }

  createAdquirente(): FormGroup {
    return this.fb.group({
      adquirente: this.fb.control<number | null>(null),
      numAccionesVendidas: [''],
      rangos: this.fb.array([this.createTransmisionRango()]),
      primaEmision: [''],
      precioTransmision: [''],
    });
  }

  addAdquirente(): void {
    this.adquirentes.push(this.createAdquirente());
  }

  removeAdquirente(index: number): void {
    if (this.adquirentes.length > 1) {
      this.adquirentes.removeAt(index);
    }
  }

  getAdquirenteRangos(index: number): FormArray {
    return this.adquirentes.at(index).get('rangos') as FormArray;
  }

  addTransmisionRango(adqIndex: number): void {
    this.getAdquirenteRangos(adqIndex).push(this.createTransmisionRango());
  }

  removeTransmisionRango(adqIndex: number, rangoIndex: number): void {
    const rangos = this.getAdquirenteRangos(adqIndex);
    if (rangos.length > 1) {
      rangos.removeAt(rangoIndex);
    }
  }

  private buildCrossTabForm(): FormGroup {
    switch (this.crossTabStepKey) {
      case 'step_denominacion':
        return this.fb.nonNullable.group({
          nuevaRazonSocial: this.fb.nonNullable.control(''),
        });

      case 'step_domicilio':
        return this.fb.nonNullable.group({
          tipo: this.fb.nonNullable.control('SOCIAL'),
          fechaAlta: this.fb.nonNullable.control(''),
          siglas: this.fb.nonNullable.control(''),
          viapublica: this.fb.nonNullable.control(''),
          numero: this.fb.nonNullable.control(''),
          escalera: this.fb.nonNullable.control(''),
          piso: this.fb.nonNullable.control(''),
          puerta: this.fb.nonNullable.control(''),
          municipio: this.fb.nonNullable.control(''),
          cp: this.fb.nonNullable.control(''),
          provincia: this.fb.nonNullable.control(''),
          pais: this.fb.nonNullable.control('ES'),
          refCatastral: this.fb.nonNullable.control(''),
          observaciones: this.fb.nonNullable.control(''),
        });

      case 'step_articulado':
        return this.fb.nonNullable.group({
          articulosModificados: this.fb.nonNullable.control(''),
          descripcionCambio: this.fb.nonNullable.control(''),
        });

      default:
        return this.fb.nonNullable.group({});
    }
  }

  // ── File handling ──────────────────────────────────────────────────────────
  onFileSelect(event: any): void {
    const files: FileList = event.target?.files ?? event.files;
    if (!files) return;
    Array.from(files as FileList).forEach(f => {
      if (!this.attachedFiles.find(existing => existing.name === f.name)) {
        this.attachedFiles.push(f);
      }
    });
  }

  removeFile(index: number): void {
    this.attachedFiles.splice(index, 1);
  }

  /** Opens a not-yet-uploaded file in a new tab (images and PDFs display inline). */
  openLocalFile(file: File): void {
    const url = URL.createObjectURL(file);
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }

  get savedDocuments(): CorporateOperationFile[] {
    return this.existingOperation?.documents ?? [];
  }

  async openSavedDocument(doc: CorporateOperationFile): Promise<void> {
    if (!this.existingOperation) return;
    try {
      const blob = await this.corporateOpsService.downloadDocument(this.existingOperation.id, doc.id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      console.error('Error opening document', e);
    }
  }

  // ── Labels ─────────────────────────────────────────────────────────────────
  get categoriaLabel(): string {
    return getCategoriaConfig(this.categoria)?.label ?? this.categoria;
  }

  get subtipoLabel(): string {
    if (!this.subtipo) return '';
    return getSubtipoConfig(this.subtipo)?.label ?? this.subtipo;
  }

  get wizardTitle(): string {
    const base = this.subtipoLabel ? `${this.categoriaLabel}: ${this.subtipoLabel}` : this.categoriaLabel;
    return this.isEditMode ? `Editar: ${base}` : base;
  }

  // ── Navigation ─────────────────────────────────────────────────────────────
  isStepCompleted(idx: number): boolean { return idx < this.currentStep(); }
  isStepActive(idx: number): boolean { return idx === this.currentStep(); }

  goToStep(idx: number): void {
    if (idx <= this.currentStep()) {
      this.showStepErrors.set(false);
      if (this.currentStepKey() === 'step_transformacion' && idx !== this.currentStep()) {
        this.syncIdentificationOperationCache();
      }
      this.currentStep.set(idx);
      this.onStepEntered(this.steps()[idx]?.key);
    }
  }

  prev(): void {
    this.showStepErrors.set(false);
    if (this.currentStep() > 0) {
      if (this.currentStepKey() === 'step_transformacion') {
        this.syncIdentificationOperationCache();
      }
      this.currentStep.update(s => s - 1);
    }
  }

  next(): void {
    if (this.stepErrors.length > 0) {
      this.showStepErrors.set(true);
      return;
    }
    this.showStepErrors.set(false);
    if (this.currentStep() < this.totalSteps() - 1) {
      if (this.currentStepKey() === 'step_transformacion') {
        this.syncIdentificationOperationCache();
      }
      const nextIdx = this.currentStep() + 1;
      this.currentStep.set(nextIdx);
      this.onStepEntered(this.steps()[nextIdx]?.key);
    }
  }

  /** Error visible al finalizar: fallo de la API o operación guardada pero no aplicada al cliente. */
  readonly saveError = signal<string | null>(null);

  private apiErrorMessage(e: any): string {
    const msg = e?.error?.message ?? e?.message;
    return Array.isArray(msg) ? msg.join('. ') : String(msg ?? 'Error desconocido');
  }

  fmt = formatShareAmountEs;

  tituloLabel(titulo: string | null | undefined): string {
    return titulo ? formatTituloLabel(titulo) : '—';
  }

  async finish(): Promise<void> {
    if (this.isSaving()) return;
    this.saveError.set(null);
    if (this.isTransformacion) {
      this.syncIdentificationOperationCache();
    }
    this.isSaving.set(true);

    try {
      const meta = this.metaForm.getRawValue();
      const crossTab = this.crossTabForm.getRawValue();

      const operationData = this.buildOperationData(crossTab);

      const commonFields = {
        deedDate: ddmmToISO(meta.fechaEscritura),
        deedConcept: meta.conceptoEscritura || undefined,
        notary: meta.notario || undefined,
        protocol: meta.protocolo || undefined,
        irus: meta.irus || undefined,
        observations: meta.observaciones || undefined,
        mercantileRegistryProvince: meta.provinciaRM || undefined,
        registrationDate: ddmmToISO(meta.fechaInscripcion),
        tome: meta.tomo || undefined,
        folio: meta.folio || undefined,
        sheet: meta.hoja || undefined,
        section: meta.seccion || undefined,
        book: meta.libro || undefined,
        inscriptionNumber: meta.numInscripcion || undefined,
        operationData: operationData || undefined,
      };

      let saved: CorporateOperation;

      if (this.isEditMode && this.existingOperation) {
        const updateDto: CorporateOperationUpdateDTO = { ...commonFields };
        saved = await this.corporateOpsService.update(this.existingOperation.id, updateDto);
      } else {
        const createDto: CorporateOperationCreateDTO = {
          clientId: this.clientId,
          categoria: this.categoria,
          subtipo:
            this.subtipo
            ?? (this.categoria === 'transformacion' ? 'cambio_forma_juridica' : undefined),
          ...commonFields,
        };
        saved = await this.corporateOpsService.create(createDto);
      }

      if (saved.sideEffectsError) {
        // La operación ya existe: los siguientes intentos la actualizan en lugar de duplicarla.
        this.existingOperation = saved;
        this.saveError.set(
          `La operación se guardó, pero no se pudo aplicar al cliente: ${saved.sideEffectsError}. `
          + 'Corrige los datos y vuelve a finalizar.',
        );
        return;
      }

      // Upload attached files sequentially
      const uploadedDocs: any[] = [];
      for (const file of this.attachedFiles) {
        try {
          const doc = await this.corporateOpsService.uploadDocument(saved.id, file);
          uploadedDocs.push(doc);
        } catch (uploadErr) {
          console.warn('Error uploading file:', file.name, uploadErr);
        }
      }
      this.attachedFiles = [];

      const snapshot = this.categoria === 'constitucion'
        ? this.buildConstitutionSnapshot(meta, operationData)
        : null;

      const op: OperacionSocietaria = {
        id: saved.id,
        categoria: this.categoria,
        subtipo: this.subtipo,
        categoriaLabel: this.categoriaLabel,
        subtipoLabel: this.subtipoLabel,
        tipoLabel: this.wizardTitle,
        fechaEscritura: ddmmToDisplay(meta.fechaEscritura),
        notario: meta.notario,
        protocolo: meta.protocolo,
        irus: meta.irus || undefined,
        observaciones: meta.observaciones || undefined,
        concepto: meta.conceptoEscritura || undefined,
        provinciaRM: meta.provinciaRM || undefined,
        fechaInscripcion: meta.fechaInscripcion || undefined,
        tomo: meta.tomo || undefined,
        folio: meta.folio || undefined,
        hoja: meta.hoja || undefined,
        seccion: meta.seccion || undefined,
        libro: meta.libro || undefined,
        numInscripcion: meta.numInscripcion || undefined,
        documentos: uploadedDocs,
        snapshot: snapshot as ConstitutionSnapshot,
        seccionesAfectadas: this.seccionesAfectadas,
        operationData: operationData ?? null,
      };

      this.complete.emit(op);
    } catch (e) {
      console.error('Error saving corporate operation', e);
      this.saveError.set(`No se pudo guardar la operación: ${this.apiErrorMessage(e)}`);
    } finally {
      this.isSaving.set(false);
    }
  }

  private buildConstitutionSnapshot(meta: Record<string, any>, operationData: Record<string, any> | null): ConstitutionSnapshot | null {
    if (!operationData) return null;

    const fmtEur = (n: number) => formatEuro(n);

    const cap = operationData['capitalSocial'] ?? {};
    const hasSeries = !!cap.hasSeries;
    const rawSeries: any[] = hasSeries && Array.isArray(cap.series) ? cap.series : [];
    const series: ConstitutionSnapshot['capitalSocial']['series'] = rawSeries.map((s: any) => ({
      nombre: s.name ?? '',
      id: s.id != null ? Number(s.id) : undefined,
      totalAcciones: s.totalShares ?? 0,
      valorNominal: Number(s.nominalValue) || 0,
      desde: s.sharesNumberFrom ?? 0,
      hasta: s.sharesNumberTo ?? 0,
      capitalSocial: (s.totalShares ?? 0) * (s.nominalValue ?? 0),
    }));
    const totalAcciones = hasSeries
      ? series.reduce((sum, s) => sum + s.totalAcciones, 0)
      : (cap.totalShares ?? 0);
    const nominalVal = cap.nominalValue ?? 0;
    const totalCapital = fmtEur(hasSeries
      ? series.reduce((sum, s) => sum + s.capitalSocial, 0)
      : totalAcciones * nominalVal);

    const capitalSnapshot: ConstitutionSnapshot['capitalSocial'] = {
      hasSeries,
      totalCapital,
      totalAcciones,
      acccionesDesde: hasSeries ? undefined : (cap.sharesNumberFrom ?? undefined),
      accionesHasta: hasSeries ? undefined : (cap.sharesNumberTo ?? undefined),
      valorNominal: nominalVal || undefined,
      series,
    };

    const rawSocios: any[] = Array.isArray(operationData['socios']) ? operationData['socios'] : [];
    const socios = mapSociosOperationToSnapshots(rawSocios, capitalSnapshot);
    const administracion = mapAdministrationToSnapshot(operationData['administracion'] ?? {});
    const rawApoderados: any[] = Array.isArray(operationData['apoderados']) ? operationData['apoderados'] : [];
    const apoderados = rawApoderados.map(a => mapOperationAttorneyToSnapshot(a));

    return {
      escritura: {
        fecha: meta['fechaEscritura'] ? ddmmToDisplay(meta['fechaEscritura']) : '',
        concepto: meta['conceptoEscritura'] ?? '',
        notario: meta['notario'] ?? '',
        protocolo: meta['protocolo'] ?? '',
        provinciaRM: meta['provinciaRM'] ?? '',
        fechaInscripcion: meta['fechaInscripcion'] ? ddmmToDisplay(meta['fechaInscripcion']) : '',
        tomo: meta['tomo'] ?? '',
        folio: meta['folio'] ?? '',
        hoja: meta['hoja'] ?? '',
        seccion: meta['seccion'] ?? '',
        libro: meta['libro'] ?? '',
        numInscripcion: meta['numInscripcion'] ?? '',
      },
      objetoSocial: {
        codigoCnae: operationData['cnaeCode'] ?? '',
        nombreCnae: this.store.cnaeCodes().find((c: any) => c.code === operationData['cnaeCode'])?.title,
        descripcion: operationData['corporateName'] ?? '',
      },
      capitalSocial: capitalSnapshot,
      socios,
      administracion,
      apoderados,
    };
  }

  private getSociosOperationPayload(): ReturnType<ConstitutionShareholdersStore['serializeSociosForOperation']> {
    return this.shareholdersStore.serializeSociosForOperation(this.capitalStore);
  }

  private buildOperationData(crossTab: Record<string, any>): Record<string, any> | null {
    if (this.categoria === 'constitucion') {
      const mainForm = this.store.form.getRawValue();
      return {
        cnaeCode: mainForm.cnaeCode,
        corporateName: mainForm.corporateName,
        capitalSocial: {
          ...this.capitalStore.buildCapitalSocialPayload(),
          series: this.capitalStore.serializeSeriesForOperation().map((s, i) => ({
            ...s,
            tempId: i,
          })),
        },
        socios: this.getSociosOperationPayload(),
        administracion: {
          administrationSystem: this.administrationStore.administrationForm.getRawValue().administrationSystem,
          members: this.administrationStore.serializeMembersForOperation(),
        },
        apoderados: this.attorniesStore.serializeAttorneysForOperation(),
      };
    }
    switch (this.subtipo) {
      case 'denominacion_social': {
        const newName = String(crossTab['nuevaRazonSocial'] ?? '').trim();
        const previousName = this.denominacionPreviousName.trim() || newName;
        return { previousName, newName };
      }
      case 'domicilio_social':
        return { ...crossTab, fechaAlta: ddmmToISO(crossTab['fechaAlta']) ?? crossTab['fechaAlta'] };
      case 'objeto_social':
        return {
          cnaeCode: this.store.form.getRawValue().cnaeCode,
          corporateName: this.store.form.getRawValue().corporateName,
        };
      case 'modificacion_serie': {
        return {
          capitalSocial: this.capitalStore.buildCapitalSocialPayload(),
          socios: this.getSociosOperationPayload(),
        };
      }
      case 'contrasplit': {
        const entry = this.reduccionEntries.at(0)?.getRawValue() ?? {};
        return {
          contrasplit: {
            tipoContrasplit: entry.tipoReduccion,
            serie: entry.serie || undefined,
            serieNombre: this.resolveSerieNombre(entry.serie),
            importeAmpliado: entry.importeReducido || undefined,
            nuevoVN: entry.nuevoVN || undefined,
            numAccionesAmortizadas: entry.numAccionesAmortizadas || undefined,
            rangos: (entry.rangos || []).filter((r: any) => r.desde || r.hasta),
          },
          capitalSocial: this.capitalStore.buildCapitalSocialPayload(),
          socios: this.getSociosOperationPayload(),
        };
      }
      case 'ampliacion_capital': {
        return {
          ...this.serializeAmpliacionDatos(),
          capitalSocial: this.capitalStore.buildCapitalSocialPayload(),
          socios: this.getSociosOperationPayload(),
        };
      }
      case 'articulado_estatutos':
        return crossTab;
      case 'fusion_socios_filial':
      case 'compraventa_donacion_herencia': {
        const transmision = this.transmisionForm.getRawValue();
        const adquirentes = (this.adquirentes.getRawValue() as Array<Record<string, any>>)
          .filter(a => a['adquirente'] || a['numAccionesVendidas'] || a['primaEmision'] || a['precioTransmision']
            || (a['rangos'] as Array<Record<string, any>>).some(r => r['desde'] || r['hasta'] || r['serie']))
          .map(a => {
            const rangos = (a['rangos'] as Array<Record<string, any>>)
              .filter(r => r['desde'] || r['hasta'] || r['serie'])
              .map(r => ({
                serie: r['serie'] || undefined,
                serieNombre: this.resolveSerieNombre(r['serie']),
                desde: r['desde'] || undefined,
                hasta: r['hasta'] || undefined,
              }));
            return {
              adquirente: a['adquirente'] || undefined,
              numAccionesVendidas: a['numAccionesVendidas'] || undefined,
              numeracionAccionesVendidas: rangos.length > 0 ? rangos : undefined,
              primaEmision: a['primaEmision'] || undefined,
              precioTransmision: a['precioTransmision'] || undefined,
            };
          });
        const bajas = (this.bajasForm.getRawValue() as Array<Record<string, any>>)
          .filter(b => b['fechaBaja'])
          .map(b => ({
            seriesId: b['seriesId'] ?? undefined,
            serieNombre: b['serie'],
            titulo: b['titulo'] || undefined,
            desde: b['desde'],
            hasta: b['hasta'],
            count: b['count'],
            fechaBaja: ddmmToISO(b['fechaBaja']),
          }));
        const first = adquirentes[0];
        return {
          tipoTransmision: transmision.tipoTransmision || undefined,
          transmitente: transmision.transmitente || undefined,
          adquirentes: adquirentes.length > 0 ? adquirentes : undefined,
          bajasParticipaciones: bajas.length > 0 ? bajas : undefined,
          // flat fields of the first adquirente kept for older readers
          adquirente: first?.adquirente,
          numAccionesVendidas: first?.numAccionesVendidas,
          numeracionAccionesVendidas: first?.numeracionAccionesVendidas,
          primaEmision: first?.primaEmision,
          precioTransmision: first?.precioTransmision,
          capitalSocial: this.capitalStore.buildCapitalSocialPayload(),
          socios: this.getSociosOperationPayload(),
        };
      }
      case 'escision':
      case 'reduccion_capital': {
        return {
          reducciones: this.serializeReduccionEntries(),
          capitalSocial: this.capitalStore.buildCapitalSocialPayload(),
          socios: this.getSociosOperationPayload(),
        };
      }
      case 'cambio_forma_juridica': {
        return this.getIdentificationOperationData();
      }
      case 'alta_baja_cambio_admin': {
        return {
          administracion: {
            administrationSystem: this.administrationStore.administrationForm.getRawValue().administrationSystem,
            members: this.administrationStore.serializeMembersForOperation(),
          },
        };
      }
      case 'alta_baja_apoderados': {
        return {
          apoderados: this.attorniesStore.serializeAttorneysForOperation(),
        };
      }
      case 'fusion_absorbente': {
        const fusion = this.fusionForm.getRawValue();
        const capitalPayload = this.capitalStore.buildCapitalSocialPayload();
        const result: Record<string, any> = {
          tipoFusion: fusion.tipoFusion || undefined,
          capitalSocial: capitalPayload,
          socios: this.getSociosOperationPayload(),
        };
        if (fusion.tipoFusion === 'con_ampliacion') {
          result['ampliacion'] = this.serializeAmpliacionDatos();
        }
        return result;
      }
      case 'operacion_acordeon': {
        const firstEntry = this.reduccionEntries.at(0)?.getRawValue() ?? {};
        const reduccion = {
          tipoReduccion: firstEntry.tipoReduccion,
          serie: firstEntry.serie || undefined,
          serieNombre: this.resolveSerieNombre(firstEntry.serie),
          importeReducido: firstEntry.importeReducido || undefined,
          nuevoVN: firstEntry.nuevoVN || undefined,
          numAccionesAmortizadas: firstEntry.numAccionesAmortizadas || undefined,
          fechaBaja: this.serializeFechaBaja(firstEntry),
          rangos: (firstEntry.rangos || []).filter((r: any) => r.desde || r.hasta),
        };
        return {
          reduccion,
          ...this.serializeAmpliacionDatos(),
          capitalSocial: this.capitalStore.buildCapitalSocialPayload(),
          socios: this.getSociosOperationPayload(),
        };
      }
      default:
        return null;
    }
  }
}
